import type { OrganizerGroup, OrganizerGroupMessage } from '@pulsify/shared/dto'
import { CalendarPlus, Send, Settings2, Ticket, Trash2, Users } from 'lucide-react'
import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  useDeleteOrganizerGroupMessage,
  useOrganizerGroupMessages,
  useSendOrganizerGroupMessage,
} from '~/entities/organizer-group/model/use-organizer-groups'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'
import { Textarea } from '~/shared/ui/textarea'
import { oldestFirst, systemLine } from '../lib/group-messages'
import { GroupFormDialog, GroupMembersDialog, ShareEventDialog } from './group-dialogs'

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(getLocale(), { dateStyle: 'short', timeStyle: 'short' })

function MessageContent({ message }: { message: OrganizerGroupMessage }) {
  if (message.deleted) {
    return <p className="text-sm italic opacity-70">{m.groupsMessageDeleted()}</p>
  }
  if (message.event) {
    return (
      <div className="space-y-1">
        {message.event.coverImageUrl ? (
          <img
            src={message.event.coverImageUrl}
            alt=""
            className="aspect-[3/2] w-56 rounded-md object-cover"
          />
        ) : null}
        <p className="font-medium text-sm">{message.event.title}</p>
        {message.event.hasTickets ? (
          <p className="flex items-center gap-1 text-xs opacity-80">
            <Ticket aria-hidden className="size-3.5" />
            {m.groupsEventHasTickets()}
          </p>
        ) : null}
      </div>
    )
  }
  if (message.imageUrl) {
    return <img src={message.imageUrl} alt="" className="w-56 rounded-md object-cover" />
  }
  if (message.kind === 'text' || message.content) {
    return <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>
  }
  return <p className="text-sm italic opacity-70">{m.groupsMessageAttachment()}</p>
}

function MessageRow({
  message,
  onDelete,
}: {
  message: OrganizerGroupMessage
  onDelete: (id: string) => void
}) {
  const line = systemLine(message)
  if (line !== null) {
    return <p className="py-1 text-center text-muted-foreground text-xs">{line}</p>
  }
  const mine = message.fromBusiness
  return (
    <div className={cn('group flex items-end gap-2', mine ? 'justify-end' : 'justify-start')}>
      {mine || message.deleted ? null : (
        <span className="sr-only">{message.senderName ?? m.groupsMemberGone()}</span>
      )}
      <div
        className={cn(
          'max-w-[75%] space-y-1 rounded-2xl px-3 py-2',
          mine ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
        )}
      >
        {mine ? null : (
          <p className="font-medium text-xs">{message.senderName ?? m.groupsMemberGone()}</p>
        )}
        <MessageContent message={message} />
        <p className="text-[11px] opacity-70">{formatTime(message.createdAt)}</p>
      </div>
      {message.deleted ? null : (
        <Button
          variant="ghost"
          size="icon-sm"
          className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
          aria-label={m.groupsMessageDelete()}
          onClick={() => onDelete(message.id)}
        >
          <Trash2 />
        </Button>
      )}
    </div>
  )
}

export function GroupThread({
  businessId,
  profileId,
  showListed,
  group,
}: {
  businessId: string
  profileId: string
  showListed: boolean
  group: OrganizerGroup
}) {
  const messages = useOrganizerGroupMessages(businessId, group.id)
  const send = useSendOrganizerGroupMessage(businessId, group.id)
  const remove = useDeleteOrganizerGroupMessage(businessId, group.id)
  const [draft, setDraft] = useState('')
  const [dialog, setDialog] = useState<'members' | 'settings' | 'event' | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const rows = oldestFirst(messages.data ?? [])
  const newest = rows.at(-1)?.id

  // biome-ignore lint/correctness/useExhaustiveDependencies: scrolls again exactly when a new message lands.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [newest])

  const submit = () => {
    const content = draft.trim()
    if (!content || send.isPending) return
    send.mutate(
      { kind: 'text', content },
      {
        onSuccess: () => setDraft(''),
        onError: (error) => toast.error(getErrorMessageFromError(error)),
      },
    )
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  const deleteMessage = (id: string) =>
    remove.mutate(id, {
      onSuccess: () => toast.success(m.groupsMessageDeletedDone()),
      onError: (error) => toast.error(getErrorMessageFromError(error)),
    })

  return (
    <Card className="flex min-h-[32rem] min-w-0 flex-col overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-border border-b px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate font-semibold text-sm">{group.title}</h2>
            {group.onlyBusinessWrites ? (
              <Badge variant="secondary">{m.groupsOnlyBusinessBadge()}</Badge>
            ) : null}
          </div>
          <p className="text-muted-foreground text-xs">
            {m.groupsMemberCount({ count: group.memberCount })}
          </p>
        </div>
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" onClick={() => setDialog('members')}>
            <Users />
            {m.groupsMembers()}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDialog('settings')}>
            <Settings2 />
            {m.groupsSettings()}
          </Button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 space-y-2 overflow-y-auto p-4"
        style={{ maxHeight: '60vh' }}
      >
        {messages.isLoading ? (
          <>
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="ml-auto h-12 w-1/2" />
          </>
        ) : (
          rows.map((message) => (
            <MessageRow key={message.id} message={message} onDelete={deleteMessage} />
          ))
        )}
      </div>

      <div className="flex items-end gap-2 border-border border-t p-3">
        <Button
          variant="outline"
          size="icon"
          aria-label={m.groupsShareEvent()}
          onClick={() => setDialog('event')}
        >
          <CalendarPlus />
        </Button>
        <Textarea
          value={draft}
          rows={1}
          maxLength={2000}
          placeholder={m.groupsComposerPlaceholder()}
          className="min-h-9 flex-1 resize-none"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          size="icon"
          aria-label={m.groupsSend()}
          disabled={!draft.trim() || send.isPending}
          onClick={submit}
        >
          <Send />
        </Button>
      </div>

      <GroupMembersDialog
        open={dialog === 'members'}
        onOpenChange={(open) => setDialog(open ? 'members' : null)}
        businessId={businessId}
        groupId={group.id}
      />
      <GroupFormDialog
        open={dialog === 'settings'}
        onOpenChange={(open) => setDialog(open ? 'settings' : null)}
        businessId={businessId}
        profileId={profileId}
        showListed={showListed}
        group={group}
      />
      <ShareEventDialog
        open={dialog === 'event'}
        onOpenChange={(open) => setDialog(open ? 'event' : null)}
        businessId={businessId}
        groupId={group.id}
        profileId={profileId}
      />
    </Card>
  )
}
